import {
  Box,
  ButtonGroup,
  IconButton,
  Stack,
  styled,
  Typography,
  useTheme,
  Tooltip,
} from '@mui/material'
import { GridColDef } from '@mui/x-data-grid'
import { format } from 'date-fns'
import enDE from 'date-fns/locale/de'
import enGB from 'date-fns/locale/en-GB'
import { useTranslation } from 'react-i18next'
import LockIcon from '@mui/icons-material/Lock'
import NoEncryptionGmailerrorredIcon from '@mui/icons-material/NoEncryptionGmailerrorred'
import EditIcon from '@mui/icons-material/Edit'
import ViewIcon from '@mui/icons-material/Visibility'
import DownloadIcon from '@mui/icons-material/Download'
import DeleteIcon from '@mui/icons-material/Delete'
import FileCopyIcon from '@mui/icons-material/FileCopy'
import { LockOpenOutlined, LockOutlineRounded } from '@mui/icons-material'
import { useAppSelector } from '../../store/hooks'
import { diversityDimensionMap, fsColors, moduleConfigMap } from '../../shared/constants'
import { DAY_PATTERN } from '../../shared/model/models'
import { getIcon } from '../common/Icons'

export default (
  currentTab: 'general' | 'legal',
  setOpenDemoModal: (value: boolean) => void,
  setIsConfirmAction: ({
    reportId,
    action,
  }: {
    reportId: number
    action: 'delete' | 'clone'
  }) => void,
  downloadReport: (reportId: number, reportName: string) => void,
  lockReport: (reportId: number, isEditable: boolean) => void,
) => {
  const { t } = useTranslation()
  const { currentTheme, currentLanguage, account } = useAppSelector((state) => state.currentUser)
  const isDemoAccount = process.env.REACT_APP_SLUG === 'demo' && !account?.isSuperuser // getIsDemoAccount()
  const theme = useTheme()
  const defaultLocale = currentLanguage === 'en' ? enGB : enDE

  const StyledIconButtonDel = styled(IconButton)(() => ({
    '&:hover svg': {
      fill: '#a84432',
      padding: 1,
    },
  }))

  const StyledIconButton = styled(IconButton)(() => ({
    '&:hover svg': {
      fill: currentTheme === 'light' ? fsColors.fsGray.colors[0] : fsColors.fsGray.colors[1],
      padding: 1,
    },
  }))

  const columns: GridColDef[] = [
    {
      field: 'reportModule',
      headerName: t('module'),
      minWidth: 50,
      sortable: true,
      editable: false,
      filterable: false,
      renderCell: (params: any) => {
        const reportIcon = getIcon(params.value, {
          color: moduleConfigMap.get(params.value)?.fsColor[
            currentTheme === 'light' ? 'primary' : 'light'
          ],
        })
        const tooltipTitle = params.value ? t(`module.name.${params.value}` as any) : ''
        return (
          <Stack sx={{ mt: 1, ml: 1.5, alignItems: 'flex-start' }}>
            <Tooltip title={<Typography>{tooltipTitle}</Typography>} placement="top">
              <Box>{reportIcon}</Box>
            </Tooltip>
          </Stack>
        )
      },
    },
    {
      field: 'reportName',
      headerName: t('name'),
      minWidth: 200,
      sortable: true,
      editable: true,
      filterable: false,
    },
    {
      field: 'locale',
      headerName: t('language'),
      minWidth: 100,
      sortable: true,
      editable: false,
      filterable: false,
      renderCell: (params: any) => params.value.toUpperCase(),
    },
    {
      field: 'beginDate',
      headerName: t(currentTab === 'general' ? 'data.from' : 'duration'),
      minWidth: 170,
      sortable: true,
      editable: false,
      filterable: false,
      renderCell: (params: any) => {
        const beginYear = new Date(params.value).getFullYear()
        const endYear = new Date(params.row.endDate).getFullYear()
        return beginYear === endYear
          ? params.value
            ? format(new Date(params.value), defaultLocale === enDE ? 'dd.MM.yyyy' : DAY_PATTERN, {
                locale: defaultLocale,
              })
            : ''
          : `${beginYear} - ${endYear}`
      },
    },
    {
      field: 'updatedAt',
      headerName: t('updated.at'),
      minWidth: 170,
      sortable: true,
      editable: false,
      filterable: false,
      renderCell: (params: any) =>
        format(new Date(params.value), defaultLocale === enDE ? 'dd.MM.yyyy' : DAY_PATTERN, {
          locale: defaultLocale,
        }),
    },
    {
      field: 'isEditable',
      headerName: t('status'),
      minWidth: 80,
      sortable: true,
      editable: false,
      filterable: false,
      renderCell: (params: any) => {
        const statusIcon = params.value ? <LockOpenOutlined /> : <LockOutlineRounded />
        return (
          <div
            style={{
              display: 'flex',
              marginTop: 8,
              marginLeft: 16,
              color: theme.palette.action.disabled,
            }}
          >
            <Tooltip title={<Typography>{params.value ? t('unlocked') : t('locked')}</Typography>} placement="top">
              <Box>{statusIcon}</Box>
            </Tooltip>
          </div>
        )
      },
    },
    {
      field: 'actions',
      headerName: t('actions'),
      minWidth: 300,
      flex: 1,
      sortable: false,
      editable: false,
      filterable: false,
      renderCell: (params: any) =>
        params.row.pdfGenerationStatus === 'in_progress' ? (
          <Box
            sx={{
              width: '100%',
              textWrap: 'wrap',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <Typography variant="body2">{t('pdf.in.progress')}</Typography>
          </Box>
        ) : (
          <ButtonGroup sx={{ mt: 0.8, gap: 2 }}>
            <Tooltip title={<Typography>{t(params.row.isEditable ? 'edit' : 'view')}</Typography>} placement="top">
              <StyledIconButton
                onClick={() =>
                  isDemoAccount
                    ? setOpenDemoModal(true)
                    : window.open(`/fs/report/edit/${params.row.id}`, '_blank')
                }
              >
                {params.row.isEditable ? <EditIcon /> : <ViewIcon />}
              </StyledIconButton>
            </Tooltip>
            <Tooltip title={<Typography>{t('clone.button.explain')}</Typography>} placement="top">
              <StyledIconButton
                onClick={() =>
                  isDemoAccount
                    ? setOpenDemoModal(true)
                    : setIsConfirmAction({ reportId: params.row.id, action: 'clone' })
                }
              >
                <FileCopyIcon />
              </StyledIconButton>
            </Tooltip>
            {isDemoAccount ? (
              <a href="/FAIR_Pay_Demo_DE.pdf" download="FAIR_Pay_Demo_DE">
                <Tooltip title={<Typography>{t('download.button.explain')}</Typography>} placement="top">
                  <StyledIconButton>
                    <DownloadIcon />
                  </StyledIconButton>
                </Tooltip>
              </a>
            ) : (
              <Tooltip
                title={<Typography>{t(
                  `download.${
                    params.row.pdfGenerationStatus === 'failed' ? 'failed.' : ''
                  }button.explain`,
                )}</Typography>}
                placement="top"
              >
                <StyledIconButton
                  onClick={() => downloadReport(params.row.id, params.row.reportName)}
                >
                  <DownloadIcon />
                </StyledIconButton>
              </Tooltip>
            )}
            {account?.isSuperuser && (
              <Tooltip
                title={<Typography>{t(`${params.row.isEditable ? '' : 'un'}lock.button.explain`)}</Typography>}
                placement="top"
              >
                <StyledIconButton onClick={() => lockReport(params.row.id, params.row.isEditable)}>
                  {params.row.isEditable ? <LockIcon /> : <NoEncryptionGmailerrorredIcon />}
                </StyledIconButton>
              </Tooltip>
            )}
            <Tooltip title={<Typography>{t('delete')}</Typography>} placement="top">
              <StyledIconButtonDel
                onClick={() =>
                  isDemoAccount
                    ? setOpenDemoModal(true)
                    : setIsConfirmAction({ reportId: params.row.id, action: 'delete' })
                }
                disabled={!params.row.isEditable}
              >
                <DeleteIcon />
              </StyledIconButtonDel>
            </Tooltip>
          </ButtonGroup>
        ),
    },
  ]

  if (currentTab === 'general') {
    columns.splice(3, 0, {
      field: 'reportTheme',
      headerName: t('diversity.dimension'),
      minWidth: 170,
      sortable: false,
      editable: false,
      filterable: false,
      renderCell: (params: any) => {
        return (
          <div style={{ display: 'flex', gap: '15px', marginTop: '4px' }}>
            {params.value.diversityDimensions
              ? params.value.diversityDimensions.map((mod: string) => (
                  <Tooltip
                    key={`tool_${mod}`}
                    title={<Typography>{t(`context.model.name.${mod}` as any)}</Typography>}
                    placement="top"
                  >
                    <Box mt={0.5} ml={0.25}>
                      {getIcon(mod, {
                        color:
                          diversityDimensionMap.get(mod)?.fsColor[
                            currentTheme === 'light' ? 'primary' : 'light'
                          ],
                      })}
                    </Box>
                  </Tooltip>
                ))
              : null}
          </div>
        )
      },
    })
  }

  return columns
}
